import React from 'react';
import _ from 'lodash';
import {useRouter} from 'next/router';
import {Disclosure} from '@headlessui/react';

import {CodeIcon, VideoCameraIcon} from '@heroicons/react/solid';
import Tooltip from '../../components/Tooltip';
import {ChevronIcon, SimpleLockIcon} from '../../components/svg';
import MDX from '../../components/MDX';
import {Tab} from '../../components/Tabs';
import {FadeOut} from '../../components/FadeOut';
import YouTubeVideo from '../../components/YouTubeVideo';
import {useAnalytics} from '../../hooks';
import {cx} from '../../util';
import Markdown from './LessonMarkdown';
import LessonCommentSection from './LessonCommentSection';
import {getBrandName} from '../../components/Brand';

const MAX_ANSWERS_SHOWN = 4;

export const Tabs = {
  QUESTION: 'question',
  SOLUTION: 'solution',
  VIDEO: 'mock-interview',
  COMMUNITY: 'community',
};

const Accordion = ({
  name, content, defaultOpen, color = 'gray', onClick,
}) => (
  <Disclosure as="div" defaultOpen={defaultOpen} onClick={onClick}>
    {({open}) => (
      <div
        className={cx('rounded-lg my-4 transition duration-50 border-2',
          color === 'green' && 'bg-green-50 border-green-100',
          color === 'yellow' && 'bg-yellow-50 border-yellow-100')}
      >
        <dt className="text-base">
          <Disclosure.Button className="py-2 pl-4 pr-3 text-left w-full flex justify-between items-center text-gray-800">
            <span className={cx('font-medium', {
              'text-green-900': color === 'green',
              'text-yellow-800': color === 'yellow',
              'text-gray-800': color === 'gray',
            })}
            >
              {name}
            </span>
            <span className="ml-6 h-7 flex items-center">
              <ChevronIcon
                className={cx(open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform')}
                aria-hidden="true"
              />
            </span>
          </Disclosure.Button>
        </dt>
        <Disclosure.Panel as="div" className="pb-3 px-4" unmount={false}>
          <Markdown>{content}</Markdown>
        </Disclosure.Panel>
      </div>
    )}
  </Disclosure>
);

const InteractiveLesson = ({
  lesson,
  course,
  hideIntroWithoutAccess = true,
  onRender,
  commentData,
  initialDraft,
  communityContent, // overrides the built-in LessonCommentSection in the Community tab
  communityCount, // overrides the Community tab's count badge
}) => {
  const {tracker} = useAnalytics();
  const free = Boolean(lesson.free);
  const defaultOpen = lesson.isWebCrawler;
  const router = useRouter();

  const isTabAvailable = {
    [Tabs.QUESTION]: true,
    [Tabs.SOLUTION]: Boolean(lesson.content && lesson.hasAccess),
    [Tabs.VIDEO]: Boolean(lesson.video && lesson.hasAccess),
    [Tabs.COMMUNITY]: Boolean(lesson.question?.id || communityContent),
  };
  const activeTab = isTabAvailable[router.query.tab] ? router.query.tab : Tabs.QUESTION;

  const setActiveTab = (tab) => {
    const query = tab === Tabs.QUESTION
      ? _.omit(router.query, 'tab')
      : {...router.query, tab};
    router.replace({pathname: router.pathname, query}, undefined, {shallow: true, scroll: false});
  };

  const onClickHint = () => {
    tracker.lessonClickHint({lesson: lesson.slug, course: course?.slug, free});
  };

  const onClickSolution = () => {
    if (!lesson.hasAccess) {
      router.push('/upgrade?src=lesson-solution');
      return;
    }
    setActiveTab(Tabs.SOLUTION);
    tracker.lessonViewSolution({lesson: lesson.slug, course: course?.slug, free});
  };

  const onClickVideo = () => {
    if (!lesson.hasAccess) {
      router.push('/upgrade?src=lesson-video');
      return;
    }
    setActiveTab(Tabs.VIDEO);
    tracker.lessonViewVideoSolution({lesson: lesson.slug, course: course?.slug, free});
  };

  const onClickCommunity = () => {
    setActiveTab(Tabs.COMMUNITY);
    tracker.lessonViewCommunityAnswers({lesson: lesson.slug, course: course?.slug, free});
  };

  const comments = commentData?.root || [];

  return (
    <div>
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <FadeOut left right>
          {/* -m-1 cancels FadeOut's inner p-1 so the tab underline still overlaps border-b */}
          <div className="-m-1">
            <div className="mt-2 -mb-px flex space-x-4" aria-label="Tabs">
              <Tab
                key={Tabs.QUESTION}
                onClick={() => setActiveTab(Tabs.QUESTION)}
                active={activeTab === Tabs.QUESTION}
                className
              >
                Question
              </Tab>
              {(lesson.content) && (
                <Tab
                  key={Tabs.SOLUTION}
                  onClick={onClickSolution}
                  active={activeTab === Tabs.SOLUTION}
                >
                  <Tooltip
                    content={`Solutions are only available to ${getBrandName()} members`}
                    placement="bottom"
                    disabled={lesson.hasAccess}
                  >
                    <div>
                      {!lesson.hasAccess ? <SimpleLockIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" /> : <CodeIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" />}
                      Solution
                    </div>
                  </Tooltip>
                </Tab>
              )}
              {(lesson.video) && (
                <Tab
                  key={Tabs.VIDEO}
                  onClick={onClickVideo}
                  active={activeTab === Tabs.VIDEO}
                >
                  <Tooltip
                    content={`Mock interviews are only available to ${getBrandName()} members`}
                    placement="bottom"
                    disabled={lesson.hasAccess}
                  >
                    <div>
                      {!lesson.hasAccess ? <SimpleLockIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" /> : <VideoCameraIcon className="w-5 h-5 pb-0.5 mr-1" fill="currentColor" />}
                      Mock Interview
                    </div>
                  </Tooltip>
                </Tab>
              )}

              {/* This won't show in Socrates question */}
              {(lesson.question?.id || communityContent) && (
                <Tab
                  key={Tabs.COMMUNITY}
                  onClick={onClickCommunity}
                  active={activeTab === Tabs.COMMUNITY}
                  count={communityCount ?? comments?.length ?? 0}
                >
                  Community
                </Tab>
              )}
            </div>
          </div>
        </FadeOut>
      </div>

      {/* Question */}
      <div className={cx({hidden: activeTab !== Tabs.QUESTION})}>
        {lesson.intro && (
          <div className={cx('prose prose-indigo', {'LessonPage--blocked': hideIntroWithoutAccess && !lesson.hasAccess})}>
            <MDX onRender={onRender}>{lesson.intro}</MDX>
          </div>
        )}

        {/* Hints and Gotchas - may be text or object {hint/gotcha: string} */}

        {lesson?.hints?.map((hint, idx) => (
          <Accordion
            key={hint.hint || hint}
            name={`Hint ${idx + 1}`}
            content={hint.hint || hint}
            color="green"
            onClick={onClickHint}
            defaultOpen={defaultOpen}
          />
        ))}

        {lesson?.gotchas?.map((gotcha, idx) => (
          <Accordion
            key={gotcha.gotcha || gotcha}
            name={`Challenge ${idx + 1}`}
            content={gotcha.gotcha || gotcha}
            color="yellow"
            onClick={onClickHint}
            defaultOpen={defaultOpen}
          />
        ))}
      </div>

      {/* Solution */}
      <div className={cx({hidden: activeTab !== Tabs.SOLUTION})}>
        {lesson.hasAccess && (
          <div className="mt-6 prose prose-indigo">
            <MDX onRender={onRender}>{lesson.content}</MDX>
          </div>
        )}
      </div>

      {/* Video Solution */}
      <div className={cx({hidden: activeTab !== Tabs.VIDEO})}>
        {lesson.hasAccess && lesson.video && (
          <div>
            <div className="rounded overflow-hidden mt-6">
              <YouTubeVideo
                videoID={lesson.video}
                onPlay={() => tracker.lessonPlayVideo({
                  lesson: lesson.slug, course: course?.slug, free,
                })}
              />
            </div>
            <div className="mt-6 prose prose-indigo">
              <MDX onRender={onRender}>{lesson.description}</MDX>
            </div>
          </div>
        )}
      </div>

      {/* Community Answers */}
      <div className={cx('mt-4', {hidden: activeTab !== Tabs.COMMUNITY})}>
        {communityContent}
        {!communityContent && lesson.question?.id && (
          <div>
            <LessonCommentSection
              questionID={lesson.question.id.toString()}
              commentData={commentData}
              free={free}
              numAnswers={MAX_ANSWERS_SHOWN}
              open={!!initialDraft}
              header="Community solutions"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveLesson;
